import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import Icon from './Icon';
import Avatar from './Avatar';
import useTheme from './useTheme';
import useWhatsAppUnread from './useWhatsAppUnread';

/**
 * App chrome: collapsible sidebar + sticky top bar.
 *
 * Add a nav item per feature; guard it with `can(...)` so users only see
 * what they can reach.
 */
export default function Layout() {
  const { user, isSuperAdmin, logout, can } = useAuth();
  const [theme, toggleTheme] = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const canChat = ['whatsapp.chats.view_all', 'whatsapp.chats.view_own', 'whatsapp.chats.initiate'].some(can);
  const unread = useWhatsAppUnread(canChat);

  const nav = [
    { to: '/', label: 'Dashboard', icon: 'dashboard', end: true, show: true },
    { to: '/crm/leads', label: 'Leads', icon: 'users', show: can('crm.leads.view') },
    { to: '/crm/calls', label: 'Calls', icon: 'phone', show: ['crm.calls.view_all', 'crm.calls.view_own'].some(can) },
    { to: '/crm/devices', label: 'Devices', icon: 'mobile', show: can('crm.devices.manage') },
    { to: '/whatsapp/chats', label: 'WhatsApp', icon: 'chat', show: canChat, badge: unread },
    { to: '/whatsapp/templates', label: 'WA Templates', icon: 'doc', show: can('whatsapp.templates.manage') },
    { to: '/whatsapp/bot', label: 'WA Bot', icon: 'bot', show: can('whatsapp.bot.configure') },
    { to: '/crm/statuses', label: 'Lead Statuses', icon: 'tag', show: can('crm.statuses.manage') },
    { to: '/crm/custom-fields', label: 'CRM Fields', icon: 'key', show: can('crm.custom-fields.manage') },
    { to: '/admins', label: 'Admins', icon: 'shield', show: can('admins.view') },
  ].filter((n) => n.show);

  const current = nav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));

  const cls = ['app-shell', collapsed && 'collapsed', mobileOpen && 'mobile-open']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls}>
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">
            <Icon name="bolt" />
          </span>
          <span className="brand-text">aptcrm</span>
        </div>

        <div className="nav-label">Menu</div>
        <nav>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} title={n.label}>
              <Icon name={n.icon} />
              <span className="nav-text">{n.label}</span>
              {n.badge > 0 && <span className="nav-badge">{n.badge > 99 ? '99+' : n.badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          <Avatar name={user?.name} />
          <div className="meta">
            <strong>{user?.name}</strong>
            <span>{isSuperAdmin ? 'Super Admin' : 'Admin'}</span>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            className="icon-btn only-mobile"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <Icon name="menu" />
          </button>
          <button
            className="icon-btn only-desktop"
            onClick={() => setCollapsed((c) => !c)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <Icon name="menu" />
          </button>
          <div className="crumbs">{current?.label || 'aptcrm'}</div>
          <div className="spacer" />
          <button
            className="icon-btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </button>
          <button className="btn btn-sm" onClick={logout}>
            <Icon name="logout" size={15} />
            Log out
          </button>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
